import React, { useState } from 'react';
import { CheckCircle, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';

export interface SectionData {
  complete: boolean;
  completeness?: number;
  fieldCount?: number;
  missingFields?: string[];
  [key: string]: any;
}

interface SectionCardProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  data: SectionData;
  defaultExpanded?: boolean;
}

const formatFieldName = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const SectionCard: React.FC<SectionCardProps> = ({
  id,
  title,
  icon,
  data,
  defaultExpanded = true
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400 italic">Not provided</span>;
    }
    if (typeof value === 'boolean') {
      return <span className="font-medium text-gray-900">{value ? 'Yes' : 'No'}</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-gray-400 italic">None</span>;
      if (typeof value[0] === 'object') {
        return (
          <div className="space-y-2">
            {value.map((item, idx) => (
              <div key={idx} className="ml-4 pl-3 border-l-2 border-gray-200 text-xs space-y-1">
                <span className="text-gray-500 font-medium">Item {idx + 1}</span>
                {Object.entries(item)
                  .filter(([_, v]) => v != null && v !== '')
                  .map(([k, v]) => (
                    <div key={k}>
                      <span className="text-gray-600">{formatFieldName(k)}:</span>{' '}
                      <span className="text-gray-900">{String(v)}</span>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        );
      }
      return <span className="font-medium text-gray-900">{value.join(', ')}</span>;
    }
    if (typeof value === 'object') {
      return (
        <div className="ml-4 pl-3 border-l-2 border-gray-200 text-xs space-y-1">
          {Object.entries(value)
            .filter(([_, v]) => v != null && v !== '')
            .map(([k, v]) => (
              <div key={k}>
                <span className="text-gray-600">{formatFieldName(k)}:</span>{' '}
                <span className="text-gray-900">{String(v)}</span>
              </div>
            ))}
        </div>
      );
    }
    return <span className="font-medium text-gray-900">{String(value)}</span>;
  };

  const displayFields = Object.entries(data).filter(
    ([key]) => !['complete', 'completeness', 'fieldCount', 'missingFields', 'lastUpdated'].includes(key)
  ).filter(([_, value]) => value !== null && value !== undefined);

  return (
    <div id={id} className="border border-gray-200 rounded-lg scroll-mt-6 bg-white">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="text-gray-600">{icon}</div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">{title}</h4>
              {data.missingFields && data.missingFields.length > 0 && (
                <p className="text-xs text-amber-600 mt-0.5">
                  {data.missingFields.length} field(s) missing
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {data.complete ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-500" />
            )}

            {displayFields.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>
            )}
          </div>
        </div>

        {isExpanded && displayFields.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {displayFields.map(([key, value]) => {
                const isComplex = Array.isArray(value) || typeof value === 'object';
                return (
                  <div key={key} className={isComplex ? 'col-span-2' : 'break-words'}>
                    {!isComplex && <span className="text-gray-600">{formatFieldName(key)}: </span>}
                    {isComplex && <div className="text-gray-600 font-medium mb-1">{formatFieldName(key)}:</div>}
                    {renderValue(value)}
                  </div>
                );
              })}
            </div>

            {data.missingFields && data.missingFields.length > 0 && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                <span className="font-medium">Missing:</span> {data.missingFields.join(', ')}
              </div>
            )}
          </div>
        )}

        {isExpanded && displayFields.length === 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center italic">No data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionCard;
